import React, { useState, useEffect, Suspense, lazy } from 'react';
import { useParams, Link } from 'react-router-dom';
import SoundButton from '../components/SoundButton/SoundButton';
import SignalBell from '../components/SignalBell/SignalBell';
import PositionSummary, { PositionSummaryHeader } from '../components/PositionSummary/PositionSummary';
import CockpitGrid from '../components/Cockpit/CockpitGrid';
// Heavy widgets are code-split — lightweight-charts and the liq-map chart
// each ship ~70-100KB gz. Lazy import + Suspense fallback keeps the
// initial /chart bundle lean.
const ChartWidgetInstance = lazy(() => import('../components/Cockpit/widgets/ChartWidgetInstance'));
const LiqMapWidgetInstance = lazy(() => import('../components/Cockpit/widgets/LiqMapWidgetInstance'));
// Derivative chart widgets are lazy too — each pulls a lightweight-charts
// pane plus a data hook that opens a poller, so we don't want them in the
// initial /chart bundle until a tab actually requests them.
const LiqVolumeWidget = lazy(() => import('../components/Cockpit/widgets/LiqVolumeWidget'));
const LongShortWidget = lazy(() => import('../components/Cockpit/widgets/LongShortWidget'));
const TradeVolumeWidget = lazy(() => import('../components/Cockpit/widgets/TradeVolumeWidget'));
import ChartHeader from '../components/Cockpit/widgets/ChartHeader';
import WhalesWidgetInstance, { WhalesHeader } from '../components/Cockpit/widgets/WhalesWidgetInstance';
import LiquidationsWidgetInstance, { LiquidationsHeader } from '../components/Cockpit/widgets/LiquidationsWidgetInstance';
import OrdersWidgetInstance, { OrdersHeader } from '../components/Cockpit/widgets/OrdersWidgetInstance';
import NewsWidgetInstance, { NewsHeader } from '../components/Cockpit/widgets/NewsWidgetInstance';
import CoinPickerHeader from '../components/Cockpit/widgets/CoinPickerHeader';
import { LiqVolumeHeader, LongShortHeader, TradeVolumeHeader } from '../components/Cockpit/widgets/DerivativeWidgetHeaders';

const WidgetSuspense = ({ children }) => (
  <Suspense fallback={<div className="cockpit-widget-pending" />}>{children}</Suspense>
);

// Hoisted to module scope so chart variant widgets receive a STABLE
// reference for `forcedIndicators`. If inlined in JSX, every render of
// the page would create a new object, defeating React.memo on the chart
// widget and forcing a re-mount cascade.
const FORCED_WHALES = { whaleBubbles: true, whaleMarkers: true, liquidations: false, orderWhales: false };
const FORCED_LIQS = { whaleBubbles: false, whaleMarkers: false, liquidations: true, orderWhales: false };
const FORCED_ORDERS = { whaleBubbles: false, whaleMarkers: false, liquidations: false, orderWhales: true };
// Each indicator-pinned chart variant gets its own stable forced-indicators
// object. Keys not listed default to falsy in CoinTerminalChart, so these
// widgets render just candles + the one indicated overlay — no whales, liqs
// or orders. Toggling on the main `chart` widget remains separately
// available via its dropdown (see DEFAULT_INDICATORS).
const FORCED_SMA_20 = { sma20: true };
const FORCED_SMA_50 = { sma50: true };
const FORCED_EMA_20 = { ema20: true };
const FORCED_EMA_50 = { ema50: true };
const FORCED_ICHIMOKU = { ichimoku: true };
import { useTradeStreamConnected } from '../components/Cockpit/data/useTradeStreamStatus';
import { useTradeStreamSounds } from '../components/Cockpit/data/useTradeStreamSounds';
import { useSharedPrice } from '../components/Cockpit/data/sharedLivePrices';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { formatNumber } from '../utils/helpers';
import { recordChartDwellSeconds, captureChartVisitIfApplicable } from '../utils/analytics';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import HyperliquidPromo from '../components/PromoButtons/HyperliquidPromo';
import BitunixPromo from '../components/PromoButtons/BitunixPromo';
import './CoinTerminalPage.css';

// Only `formatDollar` is still used by the page-level TradeTooltip — the
// list/row helpers moved to CoinTerminalSections/sectionHelpers when the
// list widgets were extracted into their own components.
function formatDollar(v) {
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v}`;
}

// --- Trade Tooltip ---
function TradeTooltip({ trade, onClose }) {
  const { localizedPath } = useLanguage();
  const { t } = useTranslation();
  if (!trade) return null;
  // Liquidations invert the side semantics vs whale trades:
  // side='sell' = LONG position liquidated (red), side='buy' = SHORT (green).
  // See server/exchangeManager.js:805 for Bybit's S='Buy' -> 'sell' mapping.
  const isLiq = !!trade.isLiquidation;
  const isLongLabel = isLiq ? trade.side === 'sell' : trade.side === 'buy';
  // Whale: long=green, short=red. Liquidation inverts: a long liq is a bearish
  // event so it shows red, mirroring the row badge / .ct-liq-dot CSS.
  const sideClass = isLiq
    ? (isLongLabel ? 'red' : 'green')
    : (isLongLabel ? 'green' : 'red');
  const detailCoin = trade.baseCoin
    || trade.symbol?.replace(/(USDT|USDC|USD|PERP)$/i, '');
  return (
    <div className="ct-tooltip visible">
      <button className="ct-tooltip-close" onClick={onClose}>&times;</button>
      <div className="ct-tooltip-symbol">{trade.baseCoin || trade.symbol}</div>
      <div className="ct-tooltip-row"><span className="ct-tooltip-label">{t('chartPage.sheet.exchange', 'Exchange')}</span><span className="ct-tooltip-value">{trade.exchange}</span></div>
      <div className="ct-tooltip-row"><span className="ct-tooltip-label">{t('chartPage.chart.price', 'Price')}</span><span className="ct-tooltip-value">${formatNumber(trade.price)}</span></div>
      <div className="ct-tooltip-row"><span className="ct-tooltip-label">{t('chartPage.sheet.volume', 'Volume')}</span><span className="ct-tooltip-value">{formatDollar(trade.dollarSize)}</span></div>
      <div className="ct-tooltip-row"><span className="ct-tooltip-label">{t('chartPage.sheet.side', 'Side')}</span><span className={`ct-tooltip-value ${sideClass}`}>{
        isLiq
          ? (isLongLabel ? t('chartPage.side.longLiq', 'LONG LIQ') : t('chartPage.side.shortLiq', 'SHORT LIQ'))
          : (isLongLabel ? t('chartPage.side.long', 'LONG') : t('chartPage.side.short', 'SHORT'))
      }</span></div>
      <Link to={localizedPath(`/price/${detailCoin}`)} className="ct-tooltip-btn">{t('chartPage.sheet.openDetails', 'Open {{coin}} details', { coin: detailCoin })}</Link>
    </div>
  );
}

// ====================== MAIN COMPONENT ======================
export default function CoinTerminalPage() {
  const { coinSymbol } = useParams();
  const coin = (coinSymbol || 'BTC').toUpperCase();
  const { t } = useTranslation();
  const { localizedPath } = useLanguage();
  const showWeex = useFeatureFlag('weex-promo', true);
  // Tooltip — opened when a widget calls `onOpenTrade`. State lives at
  // page level so a single overlay covers the whole grid.
  const [tooltipTrade, setTooltipTrade] = useState(null);

  // Live price for the URL coin — used for the document title only.
  // Shared with all cockpit widgets via sharedLivePrices (one WS for the
  // whole app instead of one per useLivePrices() consumer).
  const currentPrice = useSharedPrice(coin);

  useEffect(() => {
    if (currentPrice) {
      const priceStr = Number(currentPrice).toLocaleString('en-US', {
        maximumFractionDigits: currentPrice >= 100 ? 2 : 6,
      });
      document.title = `${coin} $${priceStr} - TraderMap`;
    } else {
      document.title = `${coin} - TraderMap`;
    }
  }, [coin, currentPrice]);

  useEffect(() => () => { document.title = 'TraderMap'; }, []);

  // The page lays itself out as a fixed 100vh column with overflow:hidden, but a
  // wrapping ticker bar (or any other element that briefly grows past viewport)
  // could let body acquire its own scrollbar — which then stacks next to the
  // cockpit-scroll scrollbar on the right edge. Lock html/body scroll while the
  // chart cockpit is mounted so only the inner scroll container can scroll.
  useEffect(() => {
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      document.documentElement.style.overflow = prevHtml;
      document.body.style.overflow = prevBody;
    };
  }, []);

  // Cumulative /chart dwell tracker — only ticks while the tab is visible.
  // Emits chart_dashboards_engaged after 10min total and
  // chart_dashboards_visited when the user leaves having spent >=30s in
  // the session with total still <10min. PostHog uses both as survey
  // triggers (see Chart dashboards feedback survey).
  useEffect(() => {
    let lastTick = Date.now();
    let sessionSec = 0;
    let pendingSec = 0;
    const FLUSH_EVERY_MS = 30_000;
    let visible = document.visibilityState === 'visible';

    const tick = () => {
      if (!visible) { lastTick = Date.now(); return; }
      const now = Date.now();
      // Cap delta at twice the flush interval — if the tab was throttled or
      // the laptop slept we don't want to attribute the entire gap to dwell.
      const delta = Math.min(Math.round((now - lastTick) / 1000), (FLUSH_EVERY_MS / 1000) * 2);
      lastTick = now;
      if (delta > 0) {
        sessionSec += delta;
        pendingSec += delta;
      }
      if (pendingSec >= 30) {
        recordChartDwellSeconds(pendingSec);
        pendingSec = 0;
      }
    };

    const onVisibilityChange = () => {
      tick();
      visible = document.visibilityState === 'visible';
      lastTick = Date.now();
    };

    const interval = setInterval(tick, FLUSH_EVERY_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      tick();
      if (pendingSec > 0) recordChartDwellSeconds(pendingSec);
      captureChartVisitIfApplicable(sessionSec);
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // Status bar reads connection state from the shared cockpit WS instead
  // of opening its own — saves one full WS connection + the ping/pong
  // heartbeat that came with it.
  const isConnected = useTradeStreamConnected();
  // Wire whale/liq tier sounds + Long/Short million alert into the
  // cockpit trade stream. Without this the SoundButton in the ticker
  // bar toggles mute on a stream that has nothing to mute.
  useTradeStreamSounds();
  const exchColor = (exchange) => {
    const map = { BINANCE: '#F0B90B', BYBIT: '#F7A600', OKX: '#e8e8e8', HYPERLIQUID: '#3b82f6', COINBASE: '#0052FF' };
    return map[exchange?.toUpperCase()] || '#666';
  };

  return (
    <div className="coin-terminal">
      {/* ===== TICKER BAR ===== */}
      <div className="ct-ticker-bar">
        <Link to={localizedPath('/trades')} className="ct-ticker-logo" aria-label={t('chartPage.homeAria', 'TraderMap home')}>
          <span className="ct-logo-text"><span className="ct-logo-brackets">[ </span>TRADERMAP.IO<span className="ct-logo-brackets"> ]</span></span>
          <span className="ct-pro-badge">PRO</span>
        </Link>
        <div className="ct-ticker-actions">
          <SoundButton coin={coin} />
          <SignalBell />
        </div>
        <HyperliquidPromo shape="square" />
        {showWeex && <BitunixPromo shape="square" />}
        <Link to={localizedPath('/terminal')} className="ct-back-btn" aria-label={t('chartPage.backToTerminal', 'Back to Terminal')}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
          <span className="ct-back-btn-text">{t('chartPage.backToTerminal', 'Back to Terminal')}</span>
        </Link>
      </div>

      {/* ===== MAIN CONTENT — cockpit dashboard =====
          Every coin-aware widget returns { body, headerExtras } so the coin
          picker lives in the WidgetFrame header (consistent UX, doesn't eat
          body space). Picker + body share state via cockpitContext —
          changing the coin in the header re-fetches the body's data. */}
      <CockpitGrid
        scope="chart"
        extraToolbar={
          <>
            <div className="ct-ticker-symbol">
              <span className="ct-ticker-current">{coin}</span>
              {currentPrice ? (
                <span className="ct-ticker-price">
                  ${Number(currentPrice).toLocaleString('en-US', { maximumFractionDigits: currentPrice >= 100 ? 2 : 6 })}
                </span>
              ) : null}
            </div>
          </>
        }
        widgetProps={{
          chart: (instanceId) => ({
            body: <WidgetSuspense><ChartWidgetInstance instanceId={instanceId} defaultCoin={coin} /></WidgetSuspense>,
            headerExtras: <ChartHeader instanceId={instanceId} defaultCoin={coin} />,
          }),
          'chart-whales': (instanceId) => ({
            body: <WidgetSuspense><ChartWidgetInstance instanceId={instanceId} defaultCoin={coin} forcedIndicators={FORCED_WHALES} /></WidgetSuspense>,
            headerExtras: <ChartHeader instanceId={instanceId} defaultCoin={coin} forcedIndicators={FORCED_WHALES} />,
          }),
          'chart-liquidations': (instanceId) => ({
            body: <WidgetSuspense><ChartWidgetInstance instanceId={instanceId} defaultCoin={coin} forcedIndicators={FORCED_LIQS} /></WidgetSuspense>,
            headerExtras: <ChartHeader instanceId={instanceId} defaultCoin={coin} forcedIndicators={FORCED_LIQS} />,
          }),
          'chart-orders': (instanceId) => ({
            body: <WidgetSuspense><ChartWidgetInstance instanceId={instanceId} defaultCoin={coin} forcedIndicators={FORCED_ORDERS} /></WidgetSuspense>,
            headerExtras: <ChartHeader instanceId={instanceId} defaultCoin={coin} forcedIndicators={FORCED_ORDERS} />,
          }),
          'chart-sma-20': (instanceId) => ({
            body: <WidgetSuspense><ChartWidgetInstance instanceId={instanceId} defaultCoin={coin} forcedIndicators={FORCED_SMA_20} /></WidgetSuspense>,
            headerExtras: <ChartHeader instanceId={instanceId} defaultCoin={coin} forcedIndicators={FORCED_SMA_20} />,
          }),
          'chart-sma-50': (instanceId) => ({
            body: <WidgetSuspense><ChartWidgetInstance instanceId={instanceId} defaultCoin={coin} forcedIndicators={FORCED_SMA_50} /></WidgetSuspense>,
            headerExtras: <ChartHeader instanceId={instanceId} defaultCoin={coin} forcedIndicators={FORCED_SMA_50} />,
          }),
          'chart-ema-20': (instanceId) => ({
            body: <WidgetSuspense><ChartWidgetInstance instanceId={instanceId} defaultCoin={coin} forcedIndicators={FORCED_EMA_20} /></WidgetSuspense>,
            headerExtras: <ChartHeader instanceId={instanceId} defaultCoin={coin} forcedIndicators={FORCED_EMA_20} />,
          }),
          'chart-ema-50': (instanceId) => ({
            body: <WidgetSuspense><ChartWidgetInstance instanceId={instanceId} defaultCoin={coin} forcedIndicators={FORCED_EMA_50} /></WidgetSuspense>,
            headerExtras: <ChartHeader instanceId={instanceId} defaultCoin={coin} forcedIndicators={FORCED_EMA_50} />,
          }),
          'chart-ichimoku': (instanceId) => ({
            body: <WidgetSuspense><ChartWidgetInstance instanceId={instanceId} defaultCoin={coin} forcedIndicators={FORCED_ICHIMOKU} /></WidgetSuspense>,
            headerExtras: <ChartHeader instanceId={instanceId} defaultCoin={coin} forcedIndicators={FORCED_ICHIMOKU} />,
          }),
          'liqmap-hyperliquid': (instanceId) => ({
            body: <WidgetSuspense><LiqMapWidgetInstance instanceId={instanceId} defaultCoin={coin} source="real" /></WidgetSuspense>,
            headerExtras: <CoinPickerHeader instanceId={instanceId} defaultCoin={coin} />,
          }),
          'liqmap-synthetic': (instanceId) => ({
            body: <WidgetSuspense><LiqMapWidgetInstance instanceId={instanceId} defaultCoin={coin} source="synthetic" /></WidgetSuspense>,
            headerExtras: <CoinPickerHeader instanceId={instanceId} defaultCoin={coin} />,
          }),
          'liqmap-combined': (instanceId) => ({
            body: <WidgetSuspense><LiqMapWidgetInstance instanceId={instanceId} defaultCoin={coin} source="combined" /></WidgetSuspense>,
            headerExtras: <CoinPickerHeader instanceId={instanceId} defaultCoin={coin} />,
          }),
          'position-summary': {
            body: <PositionSummary coin={coin} />,
            headerExtras: <PositionSummaryHeader />,
          },
          whales: (instanceId) => ({
            body: <WhalesWidgetInstance instanceId={instanceId} defaultCoin={coin} onOpenTrade={setTooltipTrade} />,
            headerExtras: <WhalesHeader instanceId={instanceId} defaultCoin={coin} />,
          }),
          liquidations: (instanceId) => ({
            body: <LiquidationsWidgetInstance instanceId={instanceId} defaultCoin={coin} onOpenTrade={setTooltipTrade} />,
            headerExtras: <LiquidationsHeader instanceId={instanceId} defaultCoin={coin} />,
          }),
          orders: (instanceId) => ({
            body: <OrdersWidgetInstance instanceId={instanceId} defaultCoin={coin} />,
            headerExtras: <OrdersHeader instanceId={instanceId} defaultCoin={coin} />,
          }),
          news: (instanceId) => ({
            body: <NewsWidgetInstance instanceId={instanceId} defaultCoin={coin} />,
            headerExtras: <NewsHeader instanceId={instanceId} defaultCoin={coin} />,
          }),
          'liq-volume': (instanceId) => ({
            body: <WidgetSuspense><LiqVolumeWidget instanceId={instanceId} defaultCoin={coin} /></WidgetSuspense>,
            headerExtras: <LiqVolumeHeader instanceId={instanceId} defaultCoin={coin} />,
          }),
          'long-short': (instanceId) => ({
            body: <WidgetSuspense><LongShortWidget instanceId={instanceId} defaultCoin={coin} /></WidgetSuspense>,
            headerExtras: <LongShortHeader instanceId={instanceId} defaultCoin={coin} />,
          }),
          'trade-volume': (instanceId) => ({
            body: <WidgetSuspense><TradeVolumeWidget instanceId={instanceId} defaultCoin={coin} /></WidgetSuspense>,
            headerExtras: <TradeVolumeHeader instanceId={instanceId} defaultCoin={coin} />,
          }),
        }}
      />

      {/* ===== STATUS BAR ===== */}
      <div className="ct-statusbar">
        <div className="ct-status-left">
          <span className="ct-status-name ct-status-secondary">{t('chartPage.status.terminal', 'TRADERMAP TERMINAL')}</span>
          <span className="ct-status-divider ct-status-secondary">|</span>
          <span className="ct-status-secondary">v2.5</span>
          <span className="ct-status-divider ct-status-secondary">|</span>
          <span className="ct-status-pair">BINANCE:{coin}USDT.P</span>
        </div>
        <div className="ct-status-right">
          <div className="ct-status-connected">
            <div className={`ct-status-dot ${isConnected ? '' : 'offline'}`} />
            <span className="ct-status-connected-text">{isConnected ? t('chartPage.status.connected', 'CONNECTED') : t('chartPage.status.disconnected', 'DISCONNECTED')}</span>
          </div>
        </div>
      </div>

      {/* Tooltip overlay */}
      {tooltipTrade && <div className="ct-tooltip-overlay" onClick={() => setTooltipTrade(null)} />}
      <TradeTooltip trade={tooltipTrade} onClose={() => setTooltipTrade(null)} />
    </div>
  );
}
